import { GoogleGenerativeAI } from "@google/generative-ai";

// Initialize the Gemini AI
const genAI = new GoogleGenerativeAI(
  process.env.REACT_APP_VISURE_DETAILS_GEMINI_API_KEY
);

// System prompt to limit AI responses to insurance domain
const SYSTEM_PROMPT = `Sei un AI Advisor specializzato nel settore assicurativo italiano. Il tuo ruolo è assistere gli utenti con:
- Analisi dei rischi aziendali
- Coperture assicurative (RC professionale, RC prodotti, Cyber risk, D&O, ecc.)
- Valutazione dei massimali assicurativi
- Interpretazione di dati finanziari in ottica assicurativa
- Suggerimenti per la prevenzione dei rischi
- Normative assicurative italiane

REGOLE IMPORTANTI:
1. Rispondi SOLO a domande relative al settore assicurativo, gestione del rischio e analisi aziendali in questo contesto
2. Se ti vengono poste domande su altri argomenti, rispondi cortesemente che puoi assistere solo su temi assicurativi
3. Usa un linguaggio professionale ma accessibile
4. Fornisci risposte concrete e pratiche
5. Quando possibile, fai riferimento ai dati della visura aziendale forniti nel contesto

Rispondi sempre in italiano.`;

/**
 * Creates a comprehensive context string from the complete report data
 * Includes data from all tabs: Sintesi, Bilanci, Score & Risk, and Generale
 * @param {Object} report - The complete report object containing all company data
 * @param {Object} aiAnalysis - The AI-generated insurance analysis (optional)
 * @returns {string} - Formatted context string with all available information
 */
const createContextFromReport = (report, aiAnalysis = null) => {
  if (!report) return "";

  const context = [];

  // ==========================================
  // TAB SINTESI - Informazioni principali
  // ==========================================
  context.push("=== INFORMAZIONI PRINCIPALI (TAB SINTESI) ===\n");

  if (report.alternateSummary) {
    const alt = report.alternateSummary;

    if (alt.businessName) context.push(`Ragione sociale: ${alt.businessName}`);
    if (alt.vatRegistrationNumber)
      context.push(`Partita IVA: ${alt.vatRegistrationNumber}`);
    if (alt.taxCode) context.push(`Codice fiscale: ${alt.taxCode}`);
    if (alt.companyRegistrationNumber)
      context.push(`CCIAA/REA: ${alt.companyRegistrationNumber}`);
    if (alt.incorporationDate)
      context.push(
        `Data di costituzione: ${new Date(
          alt.incorporationDate
        ).toLocaleDateString("it-IT")}`
      );
    if (alt.legalForm) context.push(`Forma giuridica: ${alt.legalForm}`);
    if (alt.shareCapital)
      context.push(
        `Capitale sociale: €${alt.shareCapital.toLocaleString("it-IT")}`
      );

    // Stato azienda
    if (alt.companyStatus) {
      context.push(
        `Stato azienda: ${
          alt.companyStatus.description || alt.companyStatus.status
        }`
      );
    }

    // Attività principale
    if (alt.mainActivity) {
      context.push(
        `Attività principale: ${alt.mainActivity.description} (Codice: ${alt.mainActivity.code})`
      );
    }

    // Indirizzo
    if (alt.contactAddress) {
      const addr = alt.contactAddress;
      context.push(
        `Indirizzo: ${addr.street || ""} ${addr.houseNumber || ""}, ${
          addr.postalCode || ""
        } ${addr.city || ""} ${addr.province ? "(" + addr.province + ")" : ""}`
      );
    }

    // Dipendenti
    if (alt.numberOfEmployees)
      context.push(`Numero dipendenti: ${alt.numberOfEmployees}`);

    // Contatti
    if (alt.telephone) context.push(`Telefono: ${alt.telephone}`);
    if (alt.emailAddresses) context.push(`Email: ${alt.emailAddresses}`);
  }

  // Ultimo fatturato disponibile
  if (report.companySummary?.latestTurnoverFigure?.value) {
    context.push(
      `Ultimo fatturato: €${report.companySummary.latestTurnoverFigure.value.toLocaleString(
        "it-IT"
      )} ${report.companySummary.latestTurnoverFigure.currency || ""}`
    );
  }

  // ==========================================
  // TAB BILANCI - Dati finanziari dettagliati
  // ==========================================
  if (
    report.localFinancialStatements &&
    report.localFinancialStatements.length > 0
  ) {
    context.push("\n=== DATI FINANZIARI (TAB BILANCI) ===\n");

    // Prendi gli ultimi 3 anni di bilanci
    const statements = report.localFinancialStatements.slice(0, 3);

    statements.forEach((statement, index) => {
      if (index === 0) {
        context.push(
          `--- Anno ${statement.year} (Ultimo bilancio disponibile) ---`
        );
      } else {
        context.push(`\n--- Anno ${statement.year} ---`);
      }

      // Dati patrimoniali
      if (statement.totalAssets)
        context.push(
          `Totale attività: €${statement.totalAssets.toLocaleString("it-IT")}`
        );
      if (statement.totalShareholdersEquity)
        context.push(
          `Patrimonio netto: €${statement.totalShareholdersEquity.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.totalCurrentAssets)
        context.push(
          `Attività correnti: €${statement.totalCurrentAssets.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.totalNonCurrentAssets)
        context.push(
          `Attività non correnti: €${statement.totalNonCurrentAssets.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.totalLiabilities)
        context.push(
          `Totale passività: €${statement.totalLiabilities.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.totalCurrentLiabilities)
        context.push(
          `Passività correnti: €${statement.totalCurrentLiabilities.toLocaleString(
            "it-IT"
          )}`
        );

      // Conto economico
      if (statement.totalRevenues)
        context.push(
          `Fatturato/Ricavi: €${statement.totalRevenues.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.grossProfit)
        context.push(
          `Margine operativo lordo: €${statement.grossProfit.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.ebitda)
        context.push(`EBITDA: €${statement.ebitda.toLocaleString("it-IT")}`);
      if (statement.ebit)
        context.push(`EBIT: €${statement.ebit.toLocaleString("it-IT")}`);
      if (statement.profitBeforeTax)
        context.push(
          `Risultato ante imposte: €${statement.profitBeforeTax.toLocaleString(
            "it-IT"
          )}`
        );
      if (statement.profitAfterTax)
        context.push(
          `Risultato netto: €${statement.profitAfterTax.toLocaleString(
            "it-IT"
          )}`
        );

      // Cash flow
      if (statement.cashFlow)
        context.push(
          `Cash flow: €${statement.cashFlow.toLocaleString("it-IT")}`
        );
    });
  }

  // Indicatori finanziari
  if (report.localIndicators) {
    context.push("\n=== INDICATORI FINANZIARI ===\n");
    const indicators = report.localIndicators;

    if (indicators.returnOnCapitalEmployed !== undefined) {
      context.push(
        `ROCE (Return on Capital Employed): ${indicators.returnOnCapitalEmployed}%`
      );
    }
    if (indicators.returnOnAssets !== undefined) {
      context.push(`ROA (Return on Assets): ${indicators.returnOnAssets}%`);
    }
    if (indicators.returnOnEquity !== undefined) {
      context.push(`ROE (Return on Equity): ${indicators.returnOnEquity}%`);
    }
    if (indicators.currentRatio !== undefined) {
      context.push(`Indice di liquidità corrente: ${indicators.currentRatio}`);
    }
    if (indicators.quickRatio !== undefined) {
      context.push(`Indice di liquidità immediata: ${indicators.quickRatio}`);
    }
    if (indicators.gearing !== undefined) {
      context.push(`Gearing (Leva finanziaria): ${indicators.gearing}%`);
    }
    if (indicators.debtToEquityRatio !== undefined) {
      context.push(`Debt to Equity: ${indicators.debtToEquityRatio}`);
    }
    if (indicators.equityInPercentage !== undefined) {
      context.push(`Patrimonio netto in %: ${indicators.equityInPercentage}%`);
    }
  }

  // ==========================================
  // TAB SCORE & RISK - Valutazione del rischio
  // ==========================================
  context.push("\n=== SCORE & RISK (TAB SCORE & RISK) ===\n");

  if (report.creditScore) {
    const cs = report.creditScore;

    // Score attuale
    if (cs.currentCreditRating) {
      const current = cs.currentCreditRating;

      if (current.providerValue?.value !== undefined) {
        const score = current.providerValue.value;
        context.push(`Score di rischio attuale: ${score}/100`);

        // Interpretazione dello score
        let interpretation = "";
        if (score <= 20)
          interpretation = "Rischio MOLTO ALTO - Affidabilità molto bassa";
        else if (score <= 29)
          interpretation = "Rischio ALTO - Affidabilità bassa";
        else if (score <= 50) interpretation = "Rischio MODERATO";
        else if (score <= 70)
          interpretation = "Rischio BASSO - Affidabilità alta";
        else interpretation = "Rischio MOLTO BASSO - Affidabilità molto alta";

        context.push(`Interpretazione score: ${interpretation}`);
      }

      if (current.creditLimit?.value !== undefined) {
        context.push(
          `Limite di credito raccomandato: €${current.creditLimit.value.toLocaleString(
            "it-IT"
          )} ${current.creditLimit.currency || ""}`
        );
      }

      if (current.commonValue) {
        context.push(`Rating creditizio: ${current.commonValue}`);
      }
    }

    // Score precedente per confronto
    if (cs.previousCreditRating?.providerValue?.value !== undefined) {
      context.push(
        `Score precedente: ${cs.previousCreditRating.providerValue.value}/100`
      );
    }

    if (cs.latestRatingChangeDate) {
      context.push(
        `Data ultima modifica rating: ${new Date(
          cs.latestRatingChangeDate
        ).toLocaleDateString("it-IT")}`
      );
    }
  }

  // Informazioni extra sul rischio
  if (report.companySummaryExtra) {
    context.push("\n--- Segnalazioni e rischi specifici ---");
    const extra = report.companySummaryExtra;

    if (extra.hasProtesti) context.push("⚠️ PRESENZA DI PROTESTI");
    if (extra.hasSevereProtesti) context.push("⚠️ PRESENZA DI PROTESTI GRAVI");
    if (extra.hasPrejudicials) context.push("⚠️ PRESENZA DI PREGIUDIZIEVOLI");
    if (extra.hasSeverePrejudicials)
      context.push("⚠️ PRESENZA DI PREGIUDIZIEVOLI GRAVI");
    if (extra.hasInsolvency) context.push("⚠️ PRESENZA DI INSOLVENZE");
    if (extra.hasCompaniesInsolvency)
      context.push("⚠️ INSOLVENZE IN AZIENDE COLLEGATE");
    if (extra.hasCigsEvents)
      context.push(
        "⚠️ EVENTI CIGS (Cassa Integrazione Guadagni Straordinaria)"
      );
  }

  // ==========================================
  // TAB GENERALE - Informazioni aggiuntive
  // ==========================================
  if (report.otherInformation) {
    context.push("\n=== ALTRE INFORMAZIONI (TAB GENERALE) ===\n");

    if (report.otherInformation.employeesInformation) {
      const empInfo = report.otherInformation.employeesInformation;
      if (Array.isArray(empInfo) && empInfo.length > 0) {
        context.push("Storico dipendenti:");
        empInfo.forEach((emp) => {
          if (emp.year && emp.numberOfEmployees) {
            context.push(`  ${emp.year}: ${emp.numberOfEmployees} dipendenti`);
          }
        });
      }
    }
  }

  // ==========================================
  // ANALISI ASSICURATIVA AI - Se disponibile
  // ==========================================
  if (aiAnalysis) {
    context.push("\n=== ANALISI ASSICURATIVA AI ===\n");
    
    // Risk Score complessivo
    if (aiAnalysis.riskScore !== undefined) {
      context.push(`Score di rischio assicurativo: ${aiAnalysis.riskScore}/100`);
      if (aiAnalysis.riskDescription) {
        context.push(`Livello di rischio: ${aiAnalysis.riskDescription}`);
      }
      if (aiAnalysis.riskText) {
        context.push(`Profilo di rischio: ${aiAnalysis.riskText}`);
      }
    }

    // Coperture assicurative consigliate
    if (aiAnalysis.copertureAssicurative && Array.isArray(aiAnalysis.copertureAssicurative)) {
      context.push("\n--- Coperture Assicurative Consigliate ---");
      aiAnalysis.copertureAssicurative.forEach((copertura, index) => {
        context.push(`\n${index + 1}. ${copertura.tipo}`);
        if (copertura.priorita) {
          context.push(`   Priorità: ${copertura.priorita.toUpperCase()}`);
        }
        if (copertura.massimoStimato) {
          context.push(`   Massimale consigliato: ${copertura.massimoStimato}`);
        }
        if (copertura.importo) {
          context.push(`   Premio annuo stimato: €${copertura.importo.toLocaleString('it-IT')}`);
        }
        if (copertura.descrizione) {
          context.push(`   Descrizione: ${copertura.descrizione}`);
        }
      });
    }

    // Fattori di rischio dettagliati
    if (aiAnalysis.fattoriRischio) {
      context.push("\n--- Fattori di Rischio (scala 0-10) ---");
      const fattori = aiAnalysis.fattoriRischio;
      if (fattori.finanziario !== undefined) {
        context.push(`Rischio finanziario: ${fattori.finanziario}/10`);
      }
      if (fattori.operativo !== undefined) {
        context.push(`Rischio operativo: ${fattori.operativo}/10`);
      }
      if (fattori.settoriale !== undefined) {
        context.push(`Rischio settoriale: ${fattori.settoriale}/10`);
      }
      if (fattori.dimensionale !== undefined) {
        context.push(`Rischio dimensionale: ${fattori.dimensionale}/10`);
      }
      if (fattori.geografico !== undefined) {
        context.push(`Rischio geografico: ${fattori.geografico}/10`);
      }
    }

    // Benchmark di settore
    if (aiAnalysis.benchmarkSettore) {
      context.push("\n--- Benchmark di Settore ---");
      const bench = aiAnalysis.benchmarkSettore;
      if (bench.riskScore !== undefined) {
        context.push(`Score medio di settore: ${bench.riskScore}/100`);
      }
      if (bench.fatturato !== undefined) {
        context.push(`Fatturato azienda: €${bench.fatturato.toLocaleString('it-IT')}`);
      }
      if (bench.mediaFatturato !== undefined) {
        context.push(`Fatturato medio settore: €${bench.mediaFatturato.toLocaleString('it-IT')}`);
      }
      if (bench.margineUtile) {
        context.push(`Margine di utile azienda: ${bench.margineUtile}`);
      }
      if (bench.mediaUtile) {
        context.push(`Margine medio settore: ${bench.mediaUtile}`);
      }
    }
  }

  context.push("\n=== FINE DATI AZIENDALI ===");

  return context.join("\n");
};

/**
 * Sends a message to Gemini AI and gets a response
 * @param {string} message - The user's message
 * @param {Array} conversationHistory - Previous messages in the conversation
 * @param {Object} report - The report object for context
 * @param {Object} aiAnalysis - The AI-generated insurance analysis (optional)
 * @returns {Promise<string>} - The AI's response
 */
export const sendMessageToGemini = async (
  message,
  conversationHistory = [],
  report = null,
  aiAnalysis = null
) => {
  try {
    // Get the generative model
    // Using gemini-2.5-flash-lite - faster, cheaper model optimized for chat
    // Use "gemini-2.5-flash" if you need more reasoning capability
    const model = genAI.getGenerativeModel({
      model: "gemini-2.5-flash-lite",
      systemInstruction: SYSTEM_PROMPT,
    });

    // Create context from report data and AI analysis
    const reportContext = createContextFromReport(report, aiAnalysis);

    // Build the conversation history
    const history = [];

    // Add report context if available
    if (reportContext) {
      history.push({
        role: "user",
        parts: [
          {
            text: `Ecco i dati della visura aziendale corrente:\n\n${reportContext}\n\nUtilizza questi dati per fornire risposte più accurate e contestualizzate.`,
          },
        ],
      });
      history.push({
        role: "model",
        parts: [
          {
            text: "Ho preso nota dei dati aziendali. Sono pronto a rispondere alle tue domande considerando queste informazioni.",
          },
        ],
      });
    }

    // Add previous conversation messages
    conversationHistory.forEach((msg) => {
      if (msg.role === "user") {
        history.push({
          role: "user",
          parts: [{ text: msg.text }],
        });
      } else if (msg.role === "ai") {
        history.push({
          role: "model",
          parts: [{ text: msg.text }],
        });
      }
    });

    // Start chat with history
    const chat = model.startChat({
      history,
      generationConfig: {
        maxOutputTokens: 4000,
        temperature: 0.7,
        topP: 0.8,
        topK: 40,
      },
    });

    // Send the new message
    const result = await chat.sendMessage(message);
    const response = await result.response;
    const text = response.text();

    return text;
  } catch (error) {
    console.error("Error communicating with Gemini AI:", error);

    // Handle specific error cases
    if (error.message?.includes("API key")) {
      throw new Error(
        "Errore di autenticazione con Gemini AI. Verifica la configurazione della API key."
      );
    } else if (error.message?.includes("quota")) {
      throw new Error("Quota API superata. Riprova più tardi.");
    } else if (
      error.message?.includes("overloaded") ||
      error.message?.includes("503")
    ) {
      throw new Error(
        "Il servizio AI è temporaneamente sovraccarico. Riprova tra qualche secondo."
      );
    } else if (error.message?.includes("network")) {
      throw new Error(
        "Errore di connessione. Verifica la tua connessione internet."
      );
    }

    throw new Error(
      "Si è verificato un errore nella comunicazione con l'AI. Riprova."
    );
  }
};

/**
 * Errori temporanei di Gemini (modello sovraccarico, rate limit, errori server)
 * per cui ha senso ritentare la richiesta
 */
const isTransientGeminiError = (error) => {
  const msg = error?.message || "";
  return /\[(429|500|502|503|504)\s*\]|high demand|overloaded|unavailable|fetch failed|network/i.test(
    msg
  );
};

/**
 * Esegue generateContent con retry ed exponential backoff sugli errori temporanei
 */
const generateContentWithRetry = async (model, prompt, maxAttempts = 4) => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await model.generateContent(prompt);
    } catch (error) {
      if (attempt >= maxAttempts || !isTransientGeminiError(error)) {
        throw error;
      }
      const delay = 1500 * 2 ** (attempt - 1) + Math.random() * 500;
      console.warn(
        `Gemini temporaneamente non disponibile (tentativo ${attempt}/${maxAttempts}), nuovo tentativo tra ${Math.round(delay)}ms`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

/**
 * Generates insurance analysis from report data using Gemini AI
 * @param {Object} report - The complete report object
 * @returns {Promise<Object>} - Insurance analysis with coverages, risk score, and risk factors
 */
export const generateInsuranceAnalysis = async (report) => {
  if (!report) {
    throw new Error("Report data is required");
  }

  try {
    const model = genAI.getGenerativeModel({
      model: "gemini-2.5-flash",
      systemInstruction: `Sei un esperto consulente assicurativo italiano specializzato nell'analisi del rischio aziendale. 
Il tuo compito è analizzare i dati di una visura aziendale e fornire:
1. Coperture assicurative consigliate con massimali specifici
2. Un indice di rischio assicurativo (0-100, dove 0=rischio minimo, 100=rischio massimo)
3. Fattori di rischio su 5 dimensioni (scala 0-10 per ciascuno)

Rispondi ESCLUSIVAMENTE in formato JSON valido, senza markdown o testo aggiuntivo.`,
    });

    // Create context from report
    const reportContext = createContextFromReport(report);

    const prompt = `Analizza i seguenti dati aziendali e fornisci una valutazione assicurativa completa.

${reportContext}

Fornisci la risposta in questo ESATTO formato JSON (SOLO JSON, niente altro):
{
  "riskScore": <numero da 0 a 100, INDICE DI RISCHIO COMPLESSIVO DELL'AZIENDA>,
  "riskDescription": "<descrizione breve del livello di rischio>",
  "riskColor": "<colore hex per visualizzazione>",
  "riskText": "<testo descrittivo del profilo di rischio>",
  "copertureAssicurative": [
    // Includi SEMPRE almeno 8 coperture totali e assicurati che compaiano queste 4 specifiche:
    // - "D&O (Directors & Officers) - Protezione del Management"
    // - "Cyber Risk - Protezione Digitale e Legale"
    // - "Tutela Legale"
    // - "Key Man"
    {
      "tipo": "<nome copertura (es: D&O (Directors & Officers) - Protezione del Management)>",
      "priorita": "<alta|media|bassa>",
      "importo": <premio annuo stimato in euro>,
      "descrizione": "<descrizione specifica per questa azienda>",
      "massimoStimato": "<massimale consigliato formato €X.XXX.XXX>"
    }
  ],
  "fattoriRischio": {
    "finanziario": <0-10, solidità finanziaria>,
    "operativo": <0-10, rischi operativi>,
    "settoriale": <0-10, rischio del settore>,
    "dimensionale": <0-10, rischio legato alle dimensioni>,
    "geografico": <0-10, rischio geografico>
  },
  "benchmarkSettore": {
    "riskScore": <score medio del settore per comparazione, DIVERSO da quello complessivo>,
    "mediaSector": <score medio comparativo>,
    "fatturato": <fatturato azienda>,
    "mediaFatturato": <media di settore stimata>,
    "margineUtile": "<percentuale>",
    "mediaUtile": "<percentuale media settore>"
  }
}

REGOLE IMPORTANTI:
- Il riskScore principale (primo) deve essere COERENTE con i fattori di rischio specifici dell'azienda (0-100)
- Il riskScore nel benchmarkSettore deve essere la media di settore, DIVERSO dal primo
- Suggerisci ALMENO 8 coperture assicurative; includi SEMPRE: D&O (Directors & Officers) Protezione del Management, Cyber Risk Protezione Digitale e Legale, Tutela Legale, Key Man
- I massimali devono essere proporzionati al fatturato e ai rischi identificati
- I premi annui (importo) devono essere realistici per il mercato italiano
- La priorità deve riflettere l'urgenza della copertura per questa specifica azienda
- riskColor: usa #ee2e24 (rosso) per alto rischio, #f7941d (arancione) per medio-alto, #f3c534 (giallo) per medio, #6dc797 (verde chiaro) per basso, #409a6a (verde) per molto basso`;

    const result = await generateContentWithRetry(model, prompt);
    const response = await result.response;
    let text = response.text().trim();

    // Rimuovi eventuali markdown code blocks
    text = text
      .replace(/```json\n?/g, "")
      .replace(/```\n?/g, "")
      .trim();

    // Parse JSON response
    const analysis = JSON.parse(text);

    return analysis;
  } catch (error) {
    console.error("Error generating insurance analysis:", error);

    if (error instanceof SyntaxError) {
      throw new Error(
        "Errore nel parsing della risposta AI. Formato JSON non valido."
      );
    }

    if (error.message?.includes("API key")) {
      throw new Error("Errore di autenticazione con Gemini AI.");
    }

    throw new Error(
      "Errore nella generazione dell'analisi assicurativa: " + error.message
    );
  }
};

/**
 * Validates if the API key is configured
 * @returns {boolean} - True if API key is configured
 */
export const isGeminiConfigured = () => {
  return !!process.env.REACT_APP_VISURE_DETAILS_GEMINI_API_KEY;
};
